import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { Box, Button } from '@mui/material';
import classnames from 'classnames';
import _ from 'lodash';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import auth from '../../../components/auth';
import { CommunityPostCell } from './CommunityPostCell';
import { getReplyInDMHandler } from '../utils/replyInDM';
import { useCommunityPosts } from '../hooks/useCommunityPosts';
import { PostSkeleton } from '../components/PostSkeleton';
import { LoadMorePosts } from '../components/LoadMorePosts';
import AddPost from '../components/AddPost';
import { useDeleteCommunityPost } from '../hooks/useDeleteCommunityPost';
import { useToggleReactionPost } from '../hooks/useToggleReactionPost';
import { useTogglePinPost } from '../hooks/useTogglePinPost';
import {
  ReactionType,
  useBlockUserMutation,
} from '../../../../generated/graphql';
import { useCommunityIndicator } from '../../hooks/use-community-indicator';
import { communityQueryKeys, MAX_PINNED_POSTS } from '../constants';
import { EmptyFeed } from './EmptyFeed';
import { PinnedPostsSection } from './PinnedPostsSection';
import { usePinnedPosts } from '../hooks/usePinnedPosts';
import {
  updateReactionOnEntity,
  updateReactionOnList,
} from '../utils/toggleReactionHelpers';
import { communityAnalytics } from '../utils/communityAnalytics';
import { useFeedNewPostsIndicator } from './useFeedNewPostsIndicator';

type UserData = {
  name: string;
  picture: string;
  isOwner: boolean;
};
type Props = {
  influencerId: number;
  communityId: number;
  user: UserData;
  isDmFeatureEnabled: boolean;
};

export const CommunityFeed = ({
  influencerId,
  communityId,
  user,
  isDmFeatureEnabled,
}: Props) => {
  const isAdminModerator = auth.isAdministrator() || auth.isModerator();
  const queryClient = useQueryClient();
  const deletePostMutation = useDeleteCommunityPost(communityId);
  const togglePostReaction = useToggleReactionPost(communityId, {
    skipOptimistic: true,
  });
  const togglePinPost = useTogglePinPost(communityId);
  const { data: pinnedPosts } = usePinnedPosts(communityId);
  const [blockUserMutation] = useBlockUserMutation();
  const [addPostVisible, setAddPostVisible] = useState<boolean>(true);
  const addPostRef = useRef<HTMLDivElement>(null);

  const [highlightedPostId, setHighlightedPostId] = useState<string | null>(
    null,
  );

  const pinnedPostIds = useMemo(
    () => new Set(pinnedPosts?.map((p) => p.id) ?? []),
    [pinnedPosts],
  );
  const pinLimitReached = (pinnedPosts?.length ?? 0) >= MAX_PINNED_POSTS;

  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    isInitialLoading,
    hasNextPage,
  } = useCommunityPosts(communityId);

  const flattenedPosts = useMemo(
    () => (data?.pages ?? []).flatMap((page) => page ?? []),
    [data],
  );

  const { hasNewPosts, onLoadNewPosts, scrollToTop } = useFeedNewPostsIndicator(
    { posts: flattenedPosts },
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setAddPostVisible(entry.isIntersecting);
      },
      { rootMargin: '-50px' },
    );

    if (addPostRef.current) {
      observer.observe(addPostRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasNewPosts]);

  useEffect(() => {
    communityAnalytics.trackCommunityViewed(data?.pages[0] ?? [], influencerId);
  }, [data, influencerId]);

  const { setLastSeenPostId } = useCommunityIndicator(true);
  useEffect(() => {
    if (isAdminModerator) return;
    const page = data?.pages?.[0];
    if (page && !_.isEmpty(page)) {
      setLastSeenPostId(page[0].id);
    }
  }, [data, isAdminModerator, setLastSeenPostId]);

  const onBlock = useCallback(
    async (cId: number, userId: number) => {
      return blockUserMutation({ variables: { communityId: cId, userId } })
        .then(() => {
          toast.success('User blocked successfully');
          queryClient.refetchQueries(communityQueryKeys.posts(+cId));
        })
        .catch(() => {
          toast.error('Something went wrong with blocking user');
        });
    },
    [blockUserMutation, queryClient],
  );

  const handleToggleReaction = useCallback(
    async (postId: string, type: ReactionType) => {
      const listKey = communityQueryKeys.posts(communityId);
      const postKey = communityQueryKeys.post(communityId, postId);
      const pinnedKey = communityQueryKeys.pinnedPosts(communityId);

      const previousList = queryClient.getQueryData(listKey);
      const previousPost = queryClient.getQueryData(postKey);
      const previousPinned = queryClient.getQueryData(pinnedKey);

      updateReactionOnList({
        entityId: postId,
        queryClient,
        queryKey: listKey,
        type,
      });
      updateReactionOnEntity({
        entityId: postId,
        queryClient,
        queryKey: postKey,
        type,
      });
      updateReactionOnList({
        entityId: postId,
        queryClient,
        queryKey: pinnedKey,
        type,
      });

      try {
        await togglePostReaction.mutateAsync({ type, postId });
      } catch (error) {
        queryClient.setQueryData(listKey, () => previousList);
        queryClient.setQueryData(postKey, () => previousPost);
        queryClient.setQueryData(pinnedKey, () => previousPinned);
        toast.error('Something went wrong');
      }
    },
    [communityId, queryClient, togglePostReaction],
  );

  const handleGoToPost = useCallback((postId: string) => {
    const element = document.getElementById(`post-${postId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedPostId(postId);
      setTimeout(() => setHighlightedPostId(null), 2000);
    }
  }, []);

  const hasPosts = flattenedPosts.length > 0;

  return (
    <Box>
      {/* Pinned Posts - outside white container */}
      <PinnedPostsSection
        communityId={communityId}
        influencerId={influencerId}
        isUserCommunityOwner={user.isOwner}
        isDmFeatureEnabled={isDmFeatureEnabled}
        onUnpin={(postId, post) =>
          togglePinPost.mutate({ postId, pinned: false, post })
        }
        onBlock={onBlock}
        onToggleReaction={handleToggleReaction}
        onDelete={deletePostMutation.mutate}
        onGoToPost={handleGoToPost}
      />
      <Box className="bg-white md:p-[30px] rounded-2xl">
        <Box ref={addPostRef}>
          <AddPost
            communityId={communityId}
            ownerProfilePicture={user.picture}
            ownerName={user.name}
          />
        </Box>
        {isInitialLoading ? (
          <PostSkeleton />
        ) : (
          data &&
          hasPosts && (
            <Box className="relative">
              <Box
                className={classnames(
                  'w-full flex justify-between z-[10000] top-2.5',
                  {
                    absolute: addPostVisible,
                    'fixed right-4 lg:pl-[280px]': !addPostVisible,
                  },
                )}
              >
                {!addPostVisible && <Box className="w-[100px]" />}
                {hasNewPosts && (
                  <Button
                    variant="contained"
                    className={classnames(
                      'rounded-[100px] w-[145px] h-[28px] m-auto',
                    )}
                    startIcon={
                      <FontAwesomeIcon
                        icon={faArrowUp}
                        className="pl-2.5 h-4"
                      />
                    }
                    onClick={onLoadNewPosts}
                  >
                    <span className="px-2">New Posts</span>
                  </Button>
                )}
                {!addPostVisible && (
                  <Button
                    variant="contained"
                    className="rounded-[100px] w-[80px] h-[28px]"
                    startIcon={
                      <FontAwesomeIcon
                        icon={faArrowUp}
                        className="pl-2.5 h-4"
                      />
                    }
                    onClick={scrollToTop}
                  >
                    Top
                  </Button>
                )}
              </Box>
              {flattenedPosts.map((post) => {
                const isPinned = pinnedPostIds.has(post.id);
                return (
                  <CommunityPostCell
                    influencerId={influencerId}
                    communityId={communityId}
                    isUserCommunityOwner={user.isOwner}
                    postData={post}
                    key={post.id}
                    onDelete={() => deletePostMutation.mutate(post.id)}
                    onBlock={onBlock}
                    onToggleReaction={(type) =>
                      handleToggleReaction(post.id, type)
                    }
                    onReplyInDM={getReplyInDMHandler({
                      author: post.author,
                      isOwner: user.isOwner,
                      isAdminModerator,
                      influencerId,
                      isInfluencer: auth.isInfluencer(),
                      isDmFeatureEnabled,
                    })}
                    isPinned={isPinned}
                    onPin={() =>
                      togglePinPost.mutate({
                        postId: post.id,
                        pinned: !isPinned,
                        post,
                      })
                    }
                    pinLimitReached={pinLimitReached}
                    isHighlighted={highlightedPostId === post.id}
                    isDmFeatureEnabled={isDmFeatureEnabled}
                  />
                );
              })}
            </Box>
          )
        )}
        {!isInitialLoading && hasPosts && hasNextPage && (
          <LoadMorePosts
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        )}
      </Box>
      {!isInitialLoading && !hasPosts && <EmptyFeed />}
    </Box>
  );
};
